import AILimits from "../api/model/aiLimit/AILimits";
import ErrorResponse from "../api/model/common/ErrorResponse";
import OrganizationMember from "../api/model/organizations/OrganizationMember";
import OrganizationMFA from "../api/model/organizations/OrganizationMFA";
import OrganizationSSO from "../api/model/organizations/OrganizationSSO";
import ApiWorkflowStatus from "../api/model/workflowStatuses/WorkflowStatus";
import { WorkspaceFreeTrialStatus } from "../enums";

import Model from "./model";
import NoteType from "./NoteType";
import ProjectSection, { ProjectSectionData } from "./ProjectSection";
import WorkflowStatus from "./WorkflowStatus";

export interface OrganizationData {
    _id: string;
    name: string;
    status: string;
    domain?: string;
    logo?: string;
    paymentPlan: string;
    reservedSeatCount?: number;
    subscriptionStatus: string;
    subscriptionCancelAtPeriodEnd?: boolean;
    subscriptionCancelAt?: string;
    sso?: OrganizationSSO;
    mfa?: OrganizationMFA;
    membersLoading?: boolean;
    membersError?: ErrorResponse | null;
    members?: OrganizationMember[] | null;
    workflowStatuses: (WorkflowStatus | ApiWorkflowStatus)[];
    sectionIds: string[];
    sections: ProjectSectionData[];
    complete?: boolean;
    created?: string;
    recentlyUpgradedFromUserPlan?: boolean;
    minRoleForMemberManagement?: string;
    projectLimit?: number;
    styleguideLimit?: number;
    featureFlags?: string[];
    trialEndDate?: string | null;
    freeTrialStatus?: WorkspaceFreeTrialStatus;
    freeTrialFrozenExpireDate?: string | null;
    limits?: AILimits;
    settings?: {
        aiFeaturesDisabled?: boolean;
    };
    noteTypes?: NoteType[];
}

export default class Organization extends Model {
    id: string;
    _id: string;
    name: string;
    status: string;
    domain?: string;
    logo?: string;
    paymentPlan: string;
    reservedSeatCount?: number;
    subscriptionStatus: string;
    subscriptionCancelAt?: string;
    sso?: OrganizationSSO;
    mfa?: OrganizationMFA;
    membersLoading?: boolean;
    membersError?: ErrorResponse | null;
    members?: OrganizationMember[] | null;
    workflowStatuses: WorkflowStatus[];
    sectionIds: string[];
    sections: ProjectSection[];
    complete?: boolean;
    created?: string;
    recentlyUpgradedFromUserPlan?: boolean;
    settings?: {
        aiFeaturesDisabled?: boolean;
    };
    minRoleForMemberManagement?: string;
    projectLimit?: number;
    styleguideLimit?: number;
    featureFlags?: string[];
    trialEndDate?: string | null;
    freeTrialStatus?: WorkspaceFreeTrialStatus;
    freeTrialFrozenExpireDate?: string | null;
    limits?: AILimits;
    noteTypes?: NoteType[];

    constructor(organizationData: OrganizationData) {
        super();

        const {
            _id,
            name,
            status,
            domain,
            logo,
            paymentPlan,
            reservedSeatCount,
            subscriptionStatus,
            subscriptionCancelAt,
            sso,
            mfa,
            membersLoading,
            membersError,
            members,
            workflowStatuses = [],
            sectionIds = [],
            sections = [],
            complete = false,
            created,
            recentlyUpgradedFromUserPlan,
            settings: {
                aiFeaturesDisabled
            } = {},
            minRoleForMemberManagement,
            projectLimit,
            styleguideLimit,
            featureFlags,
            trialEndDate,
            freeTrialStatus,
            freeTrialFrozenExpireDate,
            limits,
            noteTypes
        } = organizationData;

        this.id = _id;
        this._id = _id;
        this.name = name;
        this.status = status;
        this.domain = domain;
        this.logo = logo;
        this.paymentPlan = paymentPlan;
        this.reservedSeatCount = reservedSeatCount;
        this.subscriptionStatus = subscriptionStatus;
        this.subscriptionCancelAt = subscriptionCancelAt;
        this.sso = sso;
        this.mfa = mfa;
        this.membersLoading = membersLoading;
        this.membersError = membersError;
        this.members = members;
        this.workflowStatuses = workflowStatuses.map(workflowStatus => new WorkflowStatus(workflowStatus));
        this.sectionIds = sectionIds;
        this.sections = sections.map(section => new ProjectSection(section));
        this.complete = complete;
        this.created = created;
        this.recentlyUpgradedFromUserPlan = recentlyUpgradedFromUserPlan;
        this.settings = {
            aiFeaturesDisabled
        };
        this.minRoleForMemberManagement = minRoleForMemberManagement;
        this.projectLimit = projectLimit;
        this.styleguideLimit = styleguideLimit;
        this.featureFlags = featureFlags;
        this.trialEndDate = trialEndDate;
        this.freeTrialStatus = freeTrialStatus;
        this.freeTrialFrozenExpireDate = freeTrialFrozenExpireDate;
        this.limits = limits;
        if (noteTypes) {
            this.noteTypes = noteTypes.map(noteType => new NoteType({
                ...noteType,
                organizationNoteType: true
            }));
        }
    }

    get numberOfMembers() {
        return this.members?.length;
    }

    get numberOfProjects() {
        return this.sections.reduce((sum, section) =>
            sum + section.projects.length
        , 0);
    }
}
